/**
 * tm/pages/DashboardFlowPage.tsx — 대시보드 (2026-10-12 · 업무진행 설계 §5 · 4차 시안)
 *  지도사 「앞 단계가 밀리면 뒤 단계도 밀려 경과에 내 업무가 잔뜩 뜬다 · 고객이 응하지 않은 것은 경과에 안 뜨게」
 *  - 업무 하나 = 한 줄(지금 단계만) · 기한 경과 = 내 차례만 · 회신 대기 · FP요청대기는 따로(경과 숫자에 안 들어감)
 *  - 오늘 내 차례(지남 + 오늘)에서 바로 처리: ✓ 완료 · +3일 · +7일 · 회신 대기 · ⋯(보류 · 중단) — 10초 되돌리기
 *  - 숫자 카드 3 : 1 = 아래 2 : 1 칸 나눔과 같은 선 · 줄 맨 앞 배지 64 고정(오와 열)
 *  옛 DashboardPage.tsx 는 되돌리기용으로 남겨 둔다.
 */
import ConsultRemindBlock from '../components/ConsultRemindBlock';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import * as api from '../api';
import type { TMStore } from '../hooks';
import type { Company, Consultation, FP, Task } from '../types';
import { C, T, R } from '../components/ui';
import ConsultConfirmModal from '../components/ConsultConfirmModal';
import ConsultPeekFlow from '../components/ConsultPeekFlow';
import LegalDashboardCard from '../legal/LegalDashboardCard';
import GovWatchAlerts from '../components/GovWatchAlerts';   // 기다리던 공고가 나왔어요(2026-10-30)
import { FlowItem, TEAL, TEAL_DK, useFlow } from '../flow/FlowActions';
import { STAFF, allRows, buckets, claimText, dayLabel, diffDays, doneToday, kstToday, md, missingLinks, waitWho } from '../flow/flowLogic';
import type { FlowRow } from '../flow/flowLogic';

function d10(d: string | null | undefined): string { return d ? d.slice(0, 10) : ''; }
type ModalMode = null | 'confirm' | 'peek';

interface Props { store: TMStore; onNavigate: (section: string, params?: unknown) => void; me?: string }

const BADGE_W = 64;

function DayBadge({ text, tone }: { text: string; tone: 'red' | 'today' | 'week' | 'next' | 'wait' | 'old' | 'gray' }) {
  const m = { red: ['#FEF2F2', '#B91C1C'], today: ['#FEF3C7', '#92400E'], week: ['#FFF7ED', '#C2410C'], next: ['#EFF6FF', C.blue], wait: ['#E1F8F5', '#08706A'], old: [TEAL_DK, '#fff'], gray: ['#F3F4F6', '#4B5563'] }[tone];
  return (
    <span style={{ width: BADGE_W, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
      <span style={{ width: 60, textAlign: 'center', padding: '3px 0', borderRadius: R.sm, background: m[0], color: m[1], fontSize: T.xs, fontWeight: 700, whiteSpace: 'nowrap' }}>{text}</span>
    </span>
  );
}

export default function DashboardFlowPage({ store, onNavigate, me }: Props) {
  const { companies, tasks, fps, consultations, taskTemplates } = store;
  const todayStr = kstToday();
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [modalConsult, setModalConsult] = useState<Consultation | null>(null);
  const [toast, setToast] = useState('');
  const [more, setMore] = useState<null | 'overdue' | 'today' | 'mine' | 'wait' | 'week' | 'next'>(null);
  function showToast(msg: string) { setToast(msg); window.setTimeout(() => setToast(''), 3000); }
  const { run, runMany, undoNode } = useFlow(() => store.refreshTasks(), m => showToast(m));
  // 빠진 입력(2026-10-18) — 꼭 고칠 것이 있으면 맨 위 한 줄
  const [inputItems, setInputItems] = useState<api.InputCheckItem[] | null>(null);
  useEffect(() => { api.getInputCheck().then(r => { if (r.success && r.data) setInputItems(r.data.items); }).catch(() => { /* 줄 없이 */ }); }, [tasks, consultations]);
  // 전체 보기 「골라서 정리」 — 여러 업무를 한꺼번에 전체 완료 · 지우기(2026-10-12 지도사 「삭제 · 전체 완료」)
  const [pick, setPick] = useState<Set<string> | null>(null);
  const [askBulk, setAskBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const closeMore = () => { setMore(null); setPick(null); setAskBulk(false); };

  function closeModal() { setModalConsult(null); setModalMode(null); }
  async function onModalDone() { await store.refresh(); closeModal(); }

  // 담당(2026-10-18) — 실무 담당 계정은 처음에 내 업무만 · 「전체」로 바꿀 수 있다(이 기기에 기억)
  const [who, setWho] = useState<string>(() => { try { const v = localStorage.getItem('tm_dash_who:' + (me || '')); if (v) return v; } catch { /* 기억 없음 */ } return me && STAFF.includes(me) ? me : 'all'; });
  const pickWho = (v: string) => { setWho(v); try { localStorage.setItem('tm_dash_who:' + (me || ''), v); } catch { /* 기억 못 함 */ } };
  const allR = useMemo(() => allRows(tasks, companies, todayStr), [tasks, companies, todayStr]);
  const rows = useMemo(() => (who === 'all' ? allR : allR.filter(r => r.who === who)), [allR, who]);
  const whoCount = (w: string) => allR.filter(r => r.kind !== 'off' && (w === 'all' || r.who === w)).length;
  // 빠진 입력 한 줄 — 고른 담당의 몫(공통 상담 포함)
  const inputs = useMemo(() => { if (!inputItems) return null; const m = inputItems.filter(x => who === 'all' || !x.who || x.who === who); return { must: m.filter(x => x.level === 'must').length, should: m.filter(x => x.level === 'should').length }; }, [inputItems, who]);
  const b = useMemo(() => buckets(rows), [rows]);
  const mine = useMemo(() => [...b.overdue, ...b.today], [b]);
  const done = useMemo(() => doneToday(tasks, todayStr), [tasks, todayStr]);
  const miss = useMemo(() => missingLinks(companies, tasks, fps), [companies, tasks, fps]);

  const nowHM = new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(11, 16);
  // 오늘 상담은 시작 시각이 지난 뒤에만(아침부터 「결과 입력」이 뜨던 것 · 2026-10-12 검수)
  const started = (c: Consultation) => d10(c.date) < todayStr || !c.time || !/^\d{1,2}:\d{2}/.test(c.time) || c.time.padStart(5, '0').slice(0, 5) <= nowHM;
  const needResult = useMemo(() => consultations.filter(c => c.status === 'scheduled' && d10(c.date) <= todayStr && c.confirm_status !== 'tentative' && started(c))
    .sort((a, b2) => d10(b2.date).localeCompare(d10(a.date))), [consultations, todayStr, nowHM]);   // eslint-disable-line react-hooks/exhaustive-deps
  const tentative = useMemo(() => consultations.filter(c => c.status === 'scheduled' && c.confirm_status === 'tentative')
    .sort((a, b2) => d10(a.date).localeCompare(d10(b2.date))), [consultations]);

  const fpOf = (comp: Company): FP | undefined => fps.find(f => (comp.fps || []).some(cf => cf.fp_id === f.id));
  const navTask = (comp: Company, task: Task) => onNavigate('companies', { companyId: comp.id, taskId: task.id });

  const sub = (r: FlowRow, extra?: ReactNode) => {
    const fp = fpOf(r.comp);
    return (
      <div style={{ fontSize: T.xs, color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
        {r.task.icon} {r.task.type}{extra}{fp ? ` · ${fp.name} FP` : ''}
      </div>
    );
  };
  /** 첫 줄 = 업체 · 지금 단계(굵게) — 「다음에 뭘 하나」가 첫눈에 (2026-10-12 검수) */
  const nameLine = (r: FlowRow, chip?: ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      <span style={{ fontSize: T.md, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
        <span style={{ fontWeight: 600 }}>{r.comp.name}</span>{r.step ? <> · <b style={{ fontWeight: 800 }}>{r.step.name}</b></> : null}
      </span>{chip}
    </div>
  );

  const mineItem = (r: FlowRow, last: boolean) => (
    <FlowItem key={r.task.id} task={r.task} comp={r.comp} step={r.step} kind="mine" run={run} last={last} panelIndent={14 + BADGE_W + 12}
      fpPhone={fpOf(r.comp)?.phone} fpName={fpOf(r.comp)?.name} onOpen={() => navTask(r.comp, r.task)}
      head={<>
        <DayBadge text={dayLabel(r.d)} tone={(r.d as number) < 0 ? 'red' : 'today'} />
        <div onClick={() => navTask(r.comp, r.task)} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
          {nameLine(r, r.postponed > 0 ? <span style={{ padding: '2px 7px', borderRadius: R.sm, background: r.postponed >= 2 ? '#FEF3C7' : C.bg, color: r.postponed >= 2 ? '#92400E' : C.sub, fontSize: T.xs, fontWeight: 700, flexShrink: 0 }}>미룸 {r.postponed}</span> : null)}
          {sub(r)}
        </div>
      </>} />
  );

  const waitItem = (r: FlowRow, last: boolean) => {
    const old = r.waitDays >= 14;
    return (
      <FlowItem key={r.task.id} task={r.task} comp={r.comp} step={r.step} kind="wait" run={run} last={last} panelIndent={14 + BADGE_W + 12}
        fpPhone={fpOf(r.comp)?.phone} fpName={fpOf(r.comp)?.name} onOpen={() => navTask(r.comp, r.task)}
        head={<>
          <DayBadge text={`${r.waitDays}일째`} tone={old ? 'old' : 'wait'} />
          <div onClick={() => navTask(r.comp, r.task)} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
            {nameLine(r, claimText(r.ex) ? <span style={{ padding: '2px 7px', borderRadius: R.sm, background: '#FEF3C7', color: '#92400E', fontSize: T.xs, fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>{claimText(r.ex)} · 확인하세요</span> : null)}
            {/* 「회신 대기」는 칸 제목이 이미 말함 — 누구 · 물어본 것만 회색으로 · 「오래됨」은 진한 배지 하나로 */}
            {sub(r, <> · {waitWho(r.ex, r.task).replace(' 회신 대기', '')}{r.ex.wait_question ? ` · ${r.ex.wait_question}` : ''}{r.ex.nudged_at ? ` · ${md(r.ex.nudged_at)} 재촉` : ''}</>)}
          </div>
        </>} />
    );
  };

  const plainItem = (r: FlowRow, tone: 'week' | 'next', last: boolean) => (
    <div key={r.task.id} onClick={() => navTask(r.comp, r.task)} className="dash-row"
      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: last ? 'none' : `1px solid ${C.border}`, cursor: 'pointer' }}>
      <DayBadge text={dayLabel(r.d)} tone={tone} />
      <div style={{ flex: 1, minWidth: 0 }}>{nameLine(r)}{sub(r)}</div>
      <span style={{ color: C.sub, fontSize: T.lg }}>›</span>
    </div>
  );

  const list = (key: NonNullable<typeof more>): { title: string; rows: FlowRow[] } => ({
    overdue: { title: '기한 지남 (내 차례)', rows: b.overdue }, today: { title: '오늘 마감', rows: b.today }, mine: { title: '⏰ 오늘 내 차례', rows: mine },
    wait: { title: '🕓 회신 대기', rows: b.wait }, week: { title: '이번주', rows: b.week }, next: { title: '다음주', rows: b.next },
  }[key]);
  const render = (key: NonNullable<typeof more>, rs: FlowRow[]) => rs.map((r, i) => {
    const last = i === rs.length - 1;
    if (key === 'wait') return waitItem(r, last);
    if (key === 'week' || key === 'next') return plainItem(r, key, last);
    return mineItem(r, last);
  });

  const stat = (label: string, value: ReactNode, color: string, note: ReactNode, onClick?: () => void) => (
    <button onClick={onClick} disabled={!onClick}
      style={{ textAlign: 'left', padding: '14px 16px', borderRadius: R.lg, background: C.white, border: `1px solid ${C.border}`, cursor: onClick ? 'pointer' : 'default', fontFamily: 'inherit' }}>
      <div style={{ fontSize: T.sm, color: C.sub, marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 26, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        <span style={{ fontSize: T.xs, color: C.sub }}>{note}</span>
      </div>
    </button>
  );

  return (
    <div style={{ padding: 16, overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>
      <style>{`.dash-row:hover{background:#F9FAFB}`}</style>
      {modalMode === 'peek' && modalConsult && (
        <ConsultPeekFlow consultation={modalConsult} fps={store.fps} wms={store.wms} templates={taskTemplates} taskCategories={store.taskCategories}
          aiPresets={store.aiPresets} onDone={onModalDone} onCancel={closeModal} onToast={showToast} onNavigate={onNavigate} />
      )}
      {modalMode === 'confirm' && modalConsult && <ConsultConfirmModal consultation={modalConsult} onDone={onModalDone} onCancel={closeModal} onToast={showToast} />}

      {inputs && inputs.must > 0 && (
        <button type="button" onClick={() => onNavigate('inputs')}
          style={{ width: '100%', marginBottom: 12, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, borderRadius: R.md, border: '1px solid #FCA5A5', background: '#FEF2F2', cursor: 'pointer', textAlign: 'left' }}>
          <b style={{ fontSize: T.md, color: '#B91C1C', fontVariantNumeric: 'tabular-nums' }}>빠진 입력 {inputs.must}건</b>
          <span style={{ fontSize: T.sm, color: '#7F1D1D' }}>고객 · 팀장님 화면이 틀리게 보이는 업무가 있어요{inputs.should ? `. 확인할 것도 ${inputs.should}건` : ''}</span>
          <span style={{ marginLeft: 'auto', fontSize: T.sm, fontWeight: 700, color: '#B91C1C' }}>고치러 가기 ›</span>
        </button>
      )}
      <GovWatchAlerts onNavigate={onNavigate} onToast={showToast} />
      <LegalDashboardCard onOpen={p => onNavigate('legal', p)} />

      {/* 담당 — 전체 · 오재인 · 임우승 (2026-10-18) */}
      <div role="tablist" aria-label="담당" style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginRight: 2 }}>담당</span>
        {['all', ...STAFF].map(w => (
          <button key={w} type="button" role="tab" aria-selected={who === w} onClick={() => pickWho(w)}
            style={{ height: 28, padding: '0 12px', borderRadius: 14, border: `1px solid ${who === w ? C.primary : C.border}`, background: who === w ? C.primary : C.white, color: who === w ? '#fff' : C.sub, fontSize: T.xs, fontWeight: 700, cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}>
            {w === 'all' ? '전체' : w} {whoCount(w)}
          </button>
        ))}
      </div>

      {/* 숫자 카드 — 3 : 1 (아래 2 : 1 칸과 같은 선) */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          {stat('기한 지남 (내 차례)', b.overdue.length, C.red, '눌러서 보기 ›', b.overdue.length ? () => setMore('overdue') : undefined)}
          {stat('오늘 마감', b.today.length, C.text, '눌러서 보기 ›', b.today.length ? () => setMore('today') : undefined)}
          {stat('회신 대기', b.wait.length, C.text, b.claimed ? `답 왔대요 ${b.claimed}` : b.oldWait ? `오래됨 ${b.oldWait}` : '경과에 안 들어가요', b.wait.length ? () => setMore('wait') : undefined)}
        </div>
        {/* 오늘 끝낸 단계 수만 — 비율은 미루기만 해도 올라가서 뺌(2026-10-12 검수) */}
        {stat('오늘 끝낸 단계', done, C.green, done ? '잘하고 있어요' : '아직 없어요')}
      </div>
      {(miss.noFp > 0 || miss.noWm > 0) && (
        <div style={{ marginBottom: 12, padding: '8px 14px', borderRadius: R.md, background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: T.sm, color: '#92400E', display: 'flex', gap: 14 }}>
          {miss.noFp > 0 && <button onClick={() => onNavigate('companies')} style={{ border: 'none', background: 'none', color: '#92400E', fontSize: T.sm, cursor: 'pointer', padding: 0 }}>진행 중인데 담당 FP · 소개 경로가 없는 업체 <b>{miss.noFp}곳</b> ›</button>}
          {miss.noWm > 0 && <button onClick={() => onNavigate('fps')} style={{ border: 'none', background: 'none', color: '#92400E', fontSize: T.sm, cursor: 'pointer', padding: 0 }}>WM 없는 FP <b>{miss.noWm}명</b> ›</button>}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <Block title="⏰ 오늘 내 차례" accent={C.red} strong
            info="지금 단계 · 내 차례 · 기한 오늘이거나 지난 것만 — 업체 · FP 회신을 기다리는 업무는 아래 「회신 대기」">
            {mine.length === 0 ? <Empty>오늘 처리할 내 차례가 없어요</Empty> : render('mine', mine.slice(0, 5))}
            {mine.length > 5 && <MoreLink onClick={() => setMore('mine')}>전체 보기 ({mine.length}건)</MoreLink>}
          </Block>
          <Block title="🕓 회신 대기" accent={TEAL}
            info="업체 · FP 회신을 기다리는 동안은 내 지연이 아니에요(시계 멈춤) · 회신 받으면 기다린 날만큼 기한을 밀어요 · 14일 넘으면 「오래됨」">
            {b.wait.length === 0 ? <Empty>회신을 기다리는 업무가 없어요</Empty> : render('wait', b.wait.slice(0, 5))}
            {b.wait.length > 5 && <MoreLink onClick={() => setMore('wait')}>전체 보기 ({b.wait.length}건)</MoreLink>}
          </Block>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <ConsultRemindBlock onToast={showToast} wrap={(title, n, body) => (
            <Block title={title} chips={<Chip bg="#F3F4F6" fg="#4B5563">{n}건</Chip>} info="내일 동행상담 팀장님께 일정 변동 · 강조할 부분을 미리 묻는 연락 · 문자는 지도사 이름 · 번호로 · 복사는 내 카톡으로">{body}</Block>
          )} />
          <Block title="이번주" chips={<Chip bg="#F3F4F6" fg="#4B5563">{b.week.length}건</Chip>} info="지금 단계 · 내 차례 · 1~7일 남음">
            {b.week.length === 0 ? <Empty>이번주 마감 없음</Empty> : render('week', b.week.slice(0, 4))}
            {b.week.length > 4 && <MoreLink onClick={() => setMore('week')}>전체 보기 ({b.week.length}건)</MoreLink>}
          </Block>
          <Block title="다음주" chips={<Chip bg="#F3F4F6" fg="#4B5563">{b.next.length}건</Chip>} info="지금 단계 · 내 차례 · 8~14일 남음">
            {b.next.length === 0 ? <Empty>다음주 마감 없음</Empty> : render('next', b.next.slice(0, 4))}
            {b.next.length > 4 && <MoreLink onClick={() => setMore('next')}>전체 보기 ({b.next.length}건)</MoreLink>}
          </Block>
          <Block title="🤝 상담결과 미입력" chips={<Chip bg="#F3F4F6" fg="#4B5563">{needResult.length}건</Chip>} info="상담일이 지났는데 결과를 안 적은 상담">
            {needResult.length === 0 ? <Empty>모두 입력했어요</Empty> : needResult.slice(0, 5).map((c, i, arr) => {
              const dd = diffDays(todayStr, d10(c.date));
              return (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                  <DayBadge text={dayLabel(dd)} tone={dd === 0 ? 'today' : 'red'} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: T.md, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.company_name || ''}</div>
                    <div style={{ fontSize: T.xs, color: C.sub, marginTop: 2 }}>{c.fp_name ? c.fp_name + ' FP · ' : ''}{md(c.date)}{c.time ? ' ' + c.time : ''}{c.location ? ' · ' + c.location : ''}</div>
                  </div>
                  <button onClick={() => { setModalConsult(c); setModalMode('peek'); }} style={{ height: 32, padding: '0 12px', borderRadius: R.sm, border: `1px solid ${C.accent}`, background: C.white, color: C.accent, fontSize: T.sm, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>결과 입력</button>
                </div>
              );
            })}
          </Block>
          <Block title="상담 일정 미정" chips={<Chip bg="#F3F4F6" fg="#4B5563">{tentative.length}건</Chip>} info="후보 날짜만 있는 상담 — 눌러서 일정 확정">
            {tentative.length === 0 ? <Empty>미정 일정 없음</Empty> : tentative.slice(0, 5).map((c, i, arr) => (
              <div key={c.id} onClick={() => { setModalConsult(c); setModalMode('confirm'); }} className="dash-row"
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : 'none', cursor: 'pointer' }}>
                <DayBadge text={`후보 ${(c.candidate_slots || []).length}개`} tone="gray" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: T.md, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.company_name || ''}</div>
                  <div style={{ fontSize: T.xs, color: C.sub, marginTop: 2 }}>{c.fp_name ? c.fp_name + ' FP' : ''}{(c.candidate_slots || [])[0] ? ` · 가장 빠른 후보 ${md(c.candidate_slots[0].date)}` : ''}</div>
                </div>
                <span style={{ color: C.sub, fontSize: T.lg }}>›</span>
              </div>
            ))}
          </Block>
        </div>
      </div>

      {/* 최근 동행상담 — 없으면 블록째 숨김 */}
      {consultations.some(c => c.status !== 'scheduled') && <div style={{ marginTop: 12, background: C.white, borderRadius: R.lg, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: T.md, fontWeight: 700 }}>🤝 최근 동행상담</span>
          <button onClick={() => onNavigate('consultation')} style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, fontSize: T.xs, cursor: 'pointer' }}>전체 보기 ›</button>
        </div>
        {consultations.filter(c => c.status !== 'scheduled').slice(0, 3).map((c, i, arr) => (
          <div key={c.id} onClick={() => { setModalConsult(c); setModalMode('peek'); }} className="dash-row"
            style={{ padding: '9px 14px', borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : 'none', cursor: 'pointer', display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: T.sm, fontWeight: 700 }}>{c.company_name || ''}</div>
              <div style={{ fontSize: T.xs, color: C.sub }}>{md(c.date)}{c.fp_name ? ' · ' + c.fp_name + ' FP' : ''}{c.location ? ' · ' + c.location : ''}</div>
            </div>
            <span style={{ alignSelf: 'center', padding: '2px 6px', borderRadius: R.sm, background: (c.task_decisions || []).length ? C.blueLt : C.bgAlt, color: (c.task_decisions || []).length ? C.blue : C.muted, fontSize: T.xs, fontWeight: 700 }}>
              {(c.task_decisions || []).length ? `업무 ${c.task_decisions.length}건` : '기록만'}
            </span>
          </div>
        ))}
      </div>}

      {more && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={closeMore} />
          <div style={{ position: 'relative', width: 820, maxWidth: 'calc(100% - 32px)', maxHeight: '80vh', borderRadius: 12, background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: T.lg, fontWeight: 700 }}>{list(more).title} ({list(more).rows.length}건)</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                {pick && (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: T.sm, cursor: 'pointer' }}>
                    <input type="checkbox" checked={pick.size > 0 && pick.size === list(more).rows.length} onChange={e => setPick(e.target.checked ? new Set(list(more).rows.map(r => r.task.id)) : new Set())} />전체 선택
                  </label>
                )}
                <button onClick={() => { setPick(pk => (pk ? null : new Set())); setAskBulk(false); }}
                  style={{ height: 32, padding: '0 12px', borderRadius: R.sm, border: `1px solid ${pick ? C.accent : C.border}`, background: pick ? '#EFF6FF' : '#fff', color: pick ? C.accent : C.text, fontSize: T.sm, fontWeight: 700, cursor: 'pointer' }}>{pick ? '고르기 끝' : '골라서 정리'}</button>
                <button onClick={closeMore} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: C.muted }}>✕</button>
              </span>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {pick ? list(more).rows.map((r, i) => {
                const on = pick.has(r.task.id);
                const flip = () => setPick(pk => { const n = new Set(pk || []); if (n.has(r.task.id)) n.delete(r.task.id); else n.add(r.task.id); return n; });
                return (
                  <label key={r.task.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: i === list(more).rows.length - 1 ? 'none' : `1px solid ${C.border}`, cursor: 'pointer', background: on ? '#EFF6FF' : '#fff' }}>
                    <input type="checkbox" checked={on} onChange={flip} style={{ width: 18, height: 18, flexShrink: 0 }} />
                    <DayBadge text={more === 'wait' ? `${r.waitDays}일째` : dayLabel(r.d)} tone={more === 'wait' ? (r.waitDays >= 14 ? 'old' : 'wait') : (r.d as number) < 0 ? 'red' : 'today'} />
                    <div style={{ flex: 1, minWidth: 0 }}>{nameLine(r)}{sub(r)}</div>
                  </label>
                );
              }) : render(more, list(more).rows)}
            </div>
            {pick && (
              <div style={{ padding: '12px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: askBulk ? '#FEF2F2' : '#fff' }}>
                {askBulk ? (<>
                  <b style={{ fontSize: T.md, color: C.red }}>{pick.size}건을 지울까요?</b>
                  <span style={{ fontSize: T.sm, color: C.sub }}>단계도 함께 · 휴지통에 30일 보관 · 지운 뒤 10초 되돌리기</span>
                  <span style={{ flex: 1 }} />
                  <button disabled={bulkBusy} onClick={async () => { setBulkBusy(true); const ts = list(more).rows.filter(r => pick.has(r.task.id)).map(r => r.task); await runMany(ts, 'trash'); setBulkBusy(false); setAskBulk(false); setPick(new Set()); }}
                    style={{ height: 36, padding: '0 16px', borderRadius: R.sm, border: 'none', background: C.red, color: '#fff', fontSize: T.md, fontWeight: 700, cursor: 'pointer', opacity: bulkBusy ? 0.6 : 1 }}>{bulkBusy ? '지우는 중' : '지우기'}</button>
                  <button onClick={() => setAskBulk(false)} style={{ height: 36, padding: '0 14px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: '#fff', fontSize: T.md, cursor: 'pointer' }}>그대로 두기</button>
                </>) : (<>
                  <span style={{ fontSize: T.md, fontWeight: 700 }}>{pick.size}건 골랐어요</span>
                  <span style={{ flex: 1 }} />
                  <button disabled={!pick.size || bulkBusy} onClick={async () => { setBulkBusy(true); const ts = list(more).rows.filter(r => pick.has(r.task.id)).map(r => r.task); await runMany(ts, 'finish'); setBulkBusy(false); setPick(new Set()); }}
                    style={{ height: 36, padding: '0 16px', borderRadius: R.sm, border: 'none', background: '#15803D', color: '#fff', fontSize: T.md, fontWeight: 700, cursor: pick.size ? 'pointer' : 'default', opacity: pick.size && !bulkBusy ? 1 : 0.45 }}>✓ 업무 전체 완료</button>
                  <button disabled={!pick.size || bulkBusy} onClick={() => setAskBulk(true)}
                    style={{ height: 36, padding: '0 14px', borderRadius: R.sm, border: `1px solid ${C.red}`, background: '#fff', color: C.red, fontSize: T.md, fontWeight: 700, cursor: pick.size ? 'pointer' : 'default', opacity: pick.size && !bulkBusy ? 1 : 0.45 }}>지우기</button>
                </>)}
              </div>
            )}
          </div>
        </div>
      )}
      {toast && <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', padding: '10px 18px', borderRadius: R.md, background: C.red, color: '#fff', fontSize: T.md, fontWeight: 600, zIndex: 3001 }}>{toast}</div>}
      {undoNode}
    </div>
  );
}

function Block({ title, chips, info, accent, strong, children }: { title: string; chips?: ReactNode; info?: string; accent?: string; strong?: boolean; children: ReactNode }) {
  const [tip, setTip] = useState(false);
  return (
    <div style={{ background: C.white, borderRadius: R.lg, border: strong && accent ? `1px solid ${accent}55` : `1px solid ${C.border}`, boxShadow: strong && accent ? `0 0 0 1px ${accent}55` : undefined, overflow: 'visible' }}>
      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
        <span style={{ fontSize: T.md, fontWeight: 700 }}>{title}</span>{chips}
        {info && <button aria-label="설명" onMouseEnter={() => setTip(true)} onMouseLeave={() => setTip(false)} onClick={() => setTip(v => !v)}
          style={{ marginLeft: 'auto', width: 20, height: 20, borderRadius: '50%', border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: 11, cursor: 'pointer' }}>i</button>}
        {tip && info && <div style={{ position: 'absolute', right: 10, top: 34, zIndex: 30, width: 280, padding: '8px 10px', borderRadius: R.sm, background: '#111827', color: '#fff', fontSize: T.xs, lineHeight: 1.6 }}>{info}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}
function Chip({ bg, fg, children }: { bg: string; fg: string; children: ReactNode }) {
  return <span style={{ padding: '2px 7px', borderRadius: R.sm, background: bg, color: fg, fontSize: T.xs, fontWeight: 700 }}>{children}</span>;
}
function Empty({ children }: { children: string }) {
  return <div style={{ padding: '18px 14px', textAlign: 'center', color: C.sub, fontSize: T.sm }}>{children}</div>;
}
function MoreLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return <button onClick={onClick} style={{ width: '100%', padding: '10px 0', border: 'none', borderTop: `1px solid ${C.border}`, background: 'transparent', color: C.accent, fontSize: T.sm, fontWeight: 600, cursor: 'pointer' }}>{children}</button>;
}
